import { z } from 'zod';
import type { ManufacturerToken } from '@sleed/bizcommerce-api';

/**
 * Manufacturer edit form. Mirrors `ManufacturerToken` and the legacy admin popup
 * (Core/client/admin/src/pages/Catalog/Manufacturers/index.js): nm required ≤100, eurl ≤400,
 * cd/erpcd/erpcd2/landingUrl optional, static_page_url ≤2000 (overrides the storefront manufacturer link), iv/isFeatured/itp/disable_* booleans, si required,
 * FilterTemplateId optional FK, h1/mt/mk/md SEO, dsc HTML description, img1/img2 images.
 * `nm` is multilanguage server-side (writes go to the active Accept-Language, default `el`). Image
 * bytes upload out-of-band (img{1,2}File → binaryRepoImagesApi.uploadManufacturer, needs a saved id).
 *
 * NOTE: the legacy page also exposes a FAQs sub-grid and a per-country exclusions modal; those
 * auxiliary relations are not part of this editor yet (tracked as follow-ups) — this surface covers
 * the manufacturer entity itself.
 */
const nullableStr = z.string().nullable();

export const manufacturerSchema = z.object({
	id: z.number().optional(),
	nm: z.string().trim().min(1, 'validation.required').max(100, 'validation.tooLong'),
	cd: nullableStr,
	eurl: z.string().trim().max(400, 'validation.tooLong').nullable(),
	erpcd: nullableStr,
	erpcd2: nullableStr,
	landingUrl: nullableStr,
	static_page_url: z.string().trim().max(2000, 'validation.tooLong').nullable(),
	FilterTemplateId: z.number().nullable(),
	iv: z.boolean(),
	isFeatured: z.boolean(),
	itp: z.boolean(),
	si: z.number({ message: 'validation.required' }).int(),
	disable_indexation: z.boolean(),
	disable_follow: z.boolean(),
	h1: nullableStr,
	mt: nullableStr,
	mk: nullableStr,
	md: nullableStr,
	dsc: nullableStr,
	// GARAN (EU Reg. 2025/1960) — producer-level defaults; each product may override.
	garan_en: z.boolean(),
	garan_yrs: z.number().nullable(),
	img1: nullableStr,
	imgdel1: z.boolean(),
	img1File: z.instanceof(File).nullable(),
	img2: nullableStr,
	imgdel2: z.boolean(),
	img2File: z.instanceof(File).nullable()
});

export type ManufacturerFormValues = z.infer<typeof manufacturerSchema>;

export const EMPTY_MANUFACTURER: ManufacturerFormValues = {
	nm: '',
	cd: null,
	eurl: null,
	erpcd: null,
	erpcd2: null,
	landingUrl: null,
	static_page_url: null,
	FilterTemplateId: null,
	iv: true,
	isFeatured: false,
	itp: false,
	si: 0,
	disable_indexation: false,
	disable_follow: false,
	h1: null,
	mt: null,
	mk: null,
	md: null,
	dsc: null,
	garan_en: false,
	garan_yrs: null,
	img1: null,
	imgdel1: false,
	img1File: null,
	img2: null,
	imgdel2: false,
	img2File: null
};

export function tokenToForm(token: ManufacturerToken): ManufacturerFormValues {
	return {
		id: token.id,
		nm: token.nm ?? '',
		cd: token.cd ?? null,
		eurl: token.eurl ?? null,
		erpcd: token.erpcd ?? null,
		erpcd2: token.erpcd2 ?? null,
		landingUrl: token.landingUrl ?? null,
		static_page_url: token.static_page_url ?? null,
		FilterTemplateId: token.FilterTemplateId ?? null,
		iv: token.iv ?? false,
		isFeatured: token.isFeatured ?? false,
		itp: token.itp ?? false,
		si: token.si ?? 0,
		disable_indexation: token.disable_indexation ?? false,
		disable_follow: token.disable_follow ?? false,
		h1: token.h1 ?? null,
		mt: token.mt ?? null,
		mk: token.mk ?? null,
		md: token.md ?? null,
		dsc: token.dsc ?? null,
		garan_en: token.garan_en ?? false,
		garan_yrs: token.garan_yrs ?? null,
		img1: token.img1 ?? null,
		imgdel1: false,
		img1File: null,
		img2: token.img2 ?? null,
		imgdel2: false,
		img2File: null
	};
}

export function formToToken(form: ManufacturerFormValues): ManufacturerToken {
	return {
		id: form.id,
		nm: form.nm.trim(),
		cd: form.cd,
		eurl: form.eurl,
		erpcd: form.erpcd,
		erpcd2: form.erpcd2,
		landingUrl: form.landingUrl,
		static_page_url: form.static_page_url?.trim() || null,
		FilterTemplateId: form.FilterTemplateId,
		iv: form.iv,
		isFeatured: form.isFeatured,
		itp: form.itp,
		si: form.si,
		disable_indexation: form.disable_indexation,
		disable_follow: form.disable_follow,
		h1: form.h1,
		mt: form.mt,
		mk: form.mk,
		md: form.md,
		dsc: form.dsc,
		garan_en: form.garan_en,
		garan_yrs: form.garan_yrs,
		img1: form.img1,
		imgdel1: form.imgdel1,
		img2: form.img2,
		imgdel2: form.imgdel2
	};
}
